import {
  Button,
  colors,
  H5,
  Input as RawInput,
  size,
  typography,
} from '@everlywell/leaves';
import styled from 'styled-components';

type SavingProps = {
  saving: boolean;
};

const Container = styled.div`
  position: relative;
`;

const Title = styled.h2`
  font-weight: ${typography.weight.regular};
  font-size: ${size.lg}px;
  line-height: 1.58;
  color: ${colors.teal4};
  margin: 0 0 24px;

  ${(mediaQuery) => mediaQuery.theme.forTabletHorizontalUp} {
    margin-bottom: 16px;
  }
`;

const SubTitle = styled.h3`
  font-weight: ${typography.weight.light};
  font-size: 18px;
  line-height: 32px;
  font-stretch: normal;
  letter-spacing: normal;
  color: rgb(${(props) => props.theme.colors.tundora});
  margin: 0 0 16px;
`;

const Box = styled.div`
  padding: 0;
  margin: ${size.xl1}px 0;
`;

const SmallEdit = styled(Button)`
  background: transparent;
  ${typography.buttonText}
  border: 0;
  color: ${colors.green5};
  cursor: pointer;
  padding: 0;
  box-shadow: none;

  &:hover,
  &:focus,
  &:active {
    background: transparent;
    color: ${colors.green5};
  }
`;

const UpdatedButton = styled(Button)`
  outline: none;
  align-self: center;
  margin-top: 38px;
  font-size: 18.7px;
  line-height: 1.5;
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
  max-width: 230px;
  height: 50px;
  border-radius: 0;
  background: ${colors.green5};
  position: relative;

  &:hover {
    background: ${colors.teal5};
  }

  &[disabled] {
    border-color: transparent;
    cursor: not-allowed;
    background: rgba(0, 0, 0, 0.08);
    box-shadow: inset 0 0 5px 0 rgba(0, 0, 0, 0.08);
    color: ${colors.gray3};
    font-weight: ${typography.weight.bold};
  }

  &.loading {
    background: ${colors.green5} !important;
  }
`;

const ButtonRow = styled.div`
  display: flex;
  justify-content: center;

  ${(mediaQuery) => mediaQuery.theme.forTabletHorizontalUp} {
    justify-content: flex-start;
    flex: 1 0 100%;
  }
`;

const HiddenInput = styled(RawInput)`
  display: none;
`;

const InvalidAddress = styled.div`
  color: ${colors.red3};
`;

const Input = styled(RawInput)`
  text-align: left;
`;

const AddressLine = styled.div`
  margin: ${size.md}px 0;
  ${typography.bodyTextSmall}
  text-align: left;
`;

const Edit = styled.div`
  cursor: pointer;
  ${typography.buttonText}
  position: absolute;
  top: 0;
  right: 0;
`;

const SubtitleShort = styled(H5)`
  color: ${colors.gray5};
  text-align: left;
  margin-right: ${size.xl2}px;
  flex: 1;
`;

const Header = styled.div`
  display: flex;
  width: 100%;
  margin-bottom: ${size.md}px;
`;

const AddressContainer = styled.div`
  position: relative;
  width: 100%;
`;

export const ButtonSpinner = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 100%;
  border-width: 4px;
  border-color: transparent;
  border-style: solid;
  border-top-color: ${colors.green5};
  box-sizing: border-box;
  animation: opacity 0.8s linear infinite;
  visibility: ${(props: SavingProps) => (props.saving ? 'visible' : 'hidden')};
  opacity: ${(props: SavingProps) => (props.saving ? 1 : 0)};
  position: absolute;
  right: 0;

  @keyframes opacity {
    0% {
      opacity: 1;
      transform: rotate(0deg);
    }
    50% {
      transform: rotate(180deg);
      opacity: 0.4;
    }
    100% {
      opacity: 1;
      transform: rotate(360deg);
    }
  }
`;

export const LoadingSpinner = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 100%;
  border-width: 4px;
  border-color: transparent;
  border-style: solid;
  border-top-color: ${colors.green5};
  box-sizing: border-box;
  animation: opacity 0.8s linear infinite;
  position: absolute;
  left: 0;
  top: 0;

  @keyframes opacity {
    0% {
      opacity: 1;
      transform: rotate(0deg);
    }
    50% {
      transform: rotate(180deg);
      opacity: 0.4;
    }
    100% {
      opacity: 1;
      transform: rotate(360deg);
    }
  }
`;

export const ButtonText = styled.span`
  visibility: ${(props: SavingProps) => (props.saving ? 'hidden' : 'visible')};
  opacity: ${(props: SavingProps) => (props.saving ? 0 : 1)};
`;

export {
  Container,
  UpdatedButton as Button,
  SmallEdit,
  ButtonRow,
  Title,
  SubTitle,
  SubtitleShort,
  Box,
  HiddenInput,
  InvalidAddress,
  Input,
  AddressLine,
  Edit,
  Header,
  AddressContainer,
};
